import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { getGeneralHostInfo,HostInfoRow } from 'mbs-ui-shared@components/computers-shared/get-general-host-Info';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { AvailablePlanStartModes } from 'mbs-ui-shared@models/backup/available-plan-start-modes';
import { PlanInfoData } from 'mbs-ui-shared@models/backup/plan-info-model';
import Computer,{ AgentType } from 'mbs-ui-shared@models/Computer';
import { RemoteCommandResponse,RemovePlansWithDataReturnType } from 'mbs-ui-shared@models/rm/remote-command-model';
import { ComputersAbstractService } from 'mbs-ui-shared@services';
import { RmCommandsAbstractWrapper } from 'mbs-ui-shared@services/rm-commands.wrapper';
import moment from 'moment';
import { map,Observable } from 'rxjs';
import { filter,finalize,shareReplay,switchMap,tap,withLatestFrom } from 'rxjs/operators';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { ComputersBackupActions,ComputersBackupSelectors } from '../store/computersBackup';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class ComputerBackupFacade {
  constructor(
    private store: Store,
    private rmCommands: RmCommandsAbstractWrapper,
    private wizardService: RemoteManagementWizardsService,
    private computersService: ComputersAbstractService,
    private computersFacade: ComputersFacade
  ) {}

  // host info
  public hostInfoByHidDirect$ = (hid: string): Observable<HostInfoRow[]> =>
    this.computersService.getComputerHostSystemInfo(hid).pipe(
      map((data) => (data && !data.message ? getGeneralHostInfo(data, true).filter((r) => (r.visible ? r.visible() : true)) : [])),
      tap((data) => this.store.dispatch(ComputersBackupActions.loadGeneralInfoSuccess({ hid, data })))
    );
  private hostInfoByHidInternal$ = (hid: string): Observable<HostInfoRow[]> =>
    this.store.select(ComputersBackupSelectors.selectHostInfoByHid(hid));
  public isHostInfoByHidExist$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectIsHostInfoByHidExist(hid));
  private hostInfoByHidLoadingInternal$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectHostInfoByHidLoading(hid));
  public hostInfoByHidLoading$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectHostInfoByHidVisibleLoading(hid));
  public hostInfoByHidLoaded$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectHostInfoByHidLoaded(hid));
  public hostInfoByHid$ = (hid: string) =>
    this.hostInfoByHidInternal$(hid).pipe(
      withLatestFrom(this.hostInfoByHidLoadingInternal$(hid)),
      filter(([, loading]) => !loading),
      map(([data]) => data),
      shareReplay(1)
    );

  // destinations
  private destinationsByHidInternal$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectDestinationsByHid(hid));
  public isDestinationByHidExist$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectIsDestinationByHidExist(hid));
  private destinationsByHidLoadingInternal$ = (hid: string) =>
    this.store.select(ComputersBackupSelectors.selectDestinationsByHidLoading(hid));
  public destinationsByHidLoading$ = (hid: string) =>
    this.store.select(ComputersBackupSelectors.selectDestinationsByHidVisibleLoading(hid));
  public destinationsByHidLoaded$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectDestinationsByHidLoaded(hid));
  public destinationsByHid$ = (hid: string) =>
    this.destinationsByHidInternal$(hid).pipe(
      withLatestFrom(this.destinationsByHidLoadingInternal$(hid)),
      filter(([, loading]) => !loading),
      map(([data]) => data),
      shareReplay(1)
    );

  // plans
  private plansByHidInternal$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectPlansByHid(hid));
  public isPlansByHidExist$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectIsPlansByHidExist(hid));
  private plansByHidLoadingInternal$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectPlansByHidLoading(hid));
  public plansByHidLoading$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectPlansByHidVisibleLoading(hid));
  public plansByHidLoaded$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectPlansByHidLoaded(hid));
  public plansByHid$ = (hid: string) =>
    this.plansByHidInternal$(hid).pipe(
      withLatestFrom(this.plansByHidLoadingInternal$(hid)),
      filter(([, loading]) => !loading),
      map(([data]) => data),
      shareReplay(1)
    );

  public isPlanRunningById$ = (id: string) => this.store.select(ComputersBackupSelectors.selectIsPlanRunningById(id));
  public isPlanSavingById$ = (id: string) => this.store.select(ComputersBackupSelectors.selectIsPlanSavingById(id));

  // available plans
  private availablePlansByHidInternal$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectAvailablePlansByHid(hid));
  public isAvailablePlansByHidExist$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectIsAvailablePlansByHidExist(hid));
  private availablePlansByHidLoadingInternal$ = (hid: string) =>
    this.store.select(ComputersBackupSelectors.selectAvailablePlansByHidLoading(hid));
  public availablePlansByHidLoading$ = (hid: string) =>
    this.store.select(ComputersBackupSelectors.selectAvailablePlansByHidVisibleLoading(hid));
  public availablePlansLoadedByHid$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectAvailablePlansByHidLoaded(hid));
  public availablePlansByHid$ = (hid: string) =>
    this.availablePlansByHidInternal$(hid).pipe(
      withLatestFrom(this.availablePlansByHidLoadingInternal$(hid)),
      filter(([, loading]) => !loading),
      map(([data]) => data),
      shareReplay(1)
    );

  // sync process
  public syncProcessByHid$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectSyncProcessByHid(hid));

  // timezone
  public timezoneByHid$ = (hid: string) => this.store.select(ComputersBackupSelectors.selectAgentTimezoneByHid(hid));

  public clear(): void {
    this.store.dispatch(ComputersBackupActions.clear());
  }

  public loadGeneralInfo(options: { hid: string; force?: boolean; quiet?: boolean }): void {
    this.store.dispatch(ComputersBackupActions.loadGeneralInfo({ hid: options.hid, force: options?.force, quiet: options?.quiet }));
  }

  public loadDestinations(options: { hid: string; force?: boolean; quiet?: boolean }): void {
    this.store.dispatch(ComputersBackupActions.loadDestinations({ hid: options.hid, force: options?.force, quiet: options?.quiet }));
  }

  public loadPlans(options: { hid: string; force?: boolean; quiet?: boolean }): void {
    this.store.dispatch(ComputersBackupActions.loadPlans({ hid: options.hid, force: options?.force, quiet: options?.quiet }));
  }

  public getComputerPlans(hid: string): Observable<PlanInfoData> {
    return this.rmCommands.getComputerPlans(hid);
  }

  public getServiceAccountName(computer: Computer, agentType: AgentType = AgentType.Backup): Observable<string> {
    return this.rmCommands.getServiceAccountName(computer, agentType);
  }

  public changeServiceAccountName(
    computer: Computer,
    account: { accountName: string; accountPassword?: string },
    agentType: AgentType = AgentType.Backup
  ): Observable<boolean> {
    return this.rmCommands.changeServiceAccountName(computer, account, agentType);
  }

  public startPlan(computer: Computer, id: string, mode: AvailablePlanStartModes): Observable<boolean> {
    return this.rmCommands.startPlan(computer, id, mode);
  }

  public stopPlan(computer: Computer, id: string): Observable<boolean> {
    return this.rmCommands.stopPlan(computer, id);
  }

  public removePlan(computer: Computer, id: string): Observable<boolean> {
    return this.rmCommands
      .removePlan(computer, id)
      .pipe(tap(() => this.computersFacade.loadComputerByHid({ hid: computer.hid, force: true })));
  }

  public removePlanWithData(computer: Computer, ids: string[]): Observable<RemoteCommandResponse<RemovePlansWithDataReturnType>> {
    return this.rmCommands
      .removePlansWithData(computer, ids)
      .pipe(tap(() => this.computersFacade.loadComputerByHid({ hid: computer.hid, force: true })));
  }

  public clonePlansFromComputer(sourceHid: string, destinationHid: string, plansId: string[]): Observable<null> {
    return this.computersService.clonePlansFromComputer(sourceHid, destinationHid, plansId);
  }

  public clonePlan(computer: Computer, id: string, name: string): Observable<boolean> {
    return this.rmCommands
      .clonePlan(computer, id, name)
      .pipe(tap(() => this.computersFacade.loadComputerByHid({ hid: computer.hid, force: true })));
  }

  public saveLegacyPlan(plan, settings = null): Observable<any> {
    const planParam = this.wizardService.PreparePlanBeforePostRM(plan);

    this.setPlanSaving(planParam.ID, true);

    return this.wizardService.PostRMPlan(plan, settings).pipe(
      finalize(() => {
        this.setPlanSaving(planParam.ID, false);
        this.wizardService.hid && this.computersFacade.loadComputerByHid({ hid: this.wizardService.hid, force: true });
      })
    );
  }

  public getLegacyPlan(settings = null, extended = false): Observable<any> {
    return extended ? this.wizardService.GetRMPlanWithAdditionalRequests() : this.wizardService.GetRMPlanModel(settings);
  }

  public getAgentUtcOffsetValue(hid: string): Observable<number> {
    return this.isHostInfoByHidExist$(hid).pipe(
      switchMap((exist) => (exist ? this.hostInfoByHid$(hid) : this.hostInfoByHidDirect$(hid))),
      map((data) => {
        const timeZone = (data || []).find((hostInfo) => hostInfo.key === 'Time zone');
        if (!timeZone?.value) return 0;
        const regexp = /^\(UTC([+-]*2[0-3]|[+-][01][0-9]:[0-5][0-9])\)/;
        const regexpArr = regexp.exec(timeZone.value);
        const utcOffsetValue = regexpArr?.length ? regexpArr[1] : null;
        return utcOffsetValue ? moment().utcOffset(utcOffsetValue).utcOffset() : null;
      })
    );
  }

  public loadAvailablePlanTypes(options: { computer: Computer; force?: boolean }): void {
    this.store.dispatch(ComputersBackupActions.loadAvailablePlans(options));
  }

  public setPlanSaving(id: string, saving = true): void {
    this.store.dispatch(ComputersBackupActions.setPlanSaving({ id, saving }));
  }

  public setPlanRunning(id: string, running = true): void {
    this.store.dispatch(ComputersBackupActions.setPlanRunning({ id, running }));
  }

  public loadSyncStatus(hid: string): void {
    this.store.dispatch(ComputersBackupActions.loadSyncStatus({ hid }));
  }
}
